import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  SvgIcon,
  Typography,
} from '@mui/material';
import { useGlobalState } from '../../../utils/GlobalStateProvider';
import { Product } from '../../../cms/types';
import {
  PRODUCT_CODE_MAPPING,
  SHOPIFY_VARIANT_MAPPINGS,
  SHOPIFY_WEB_URL,
} from '../../../config';
import { HormoneSnapshot } from '../../../types';
import { makeStyles } from '@mui/styles';
import { ReactComponent as ChevronIcon } from '../../../images/chevron-right.svg';
import { colors } from '../../../utils/theme';
import { RootineImage } from '../../../atoms/RootineImage';
import { CustomIcon } from '../../../atoms';
import RootineButton from '../../../atoms/RootineButton';
import { RootineDivider } from '../../../atoms/RootineDivider';

const useStyles = makeStyles(() => ({
  expandIcon: {
    '&$expanded': {
      transform: 'rotate(90deg)',
      marginTop: 5,
    },
  },
  expanded: {},
}));

export interface ProductVariantMapping {
  [key: string]: string | undefined;
}

const recommendedProducts: { [name: string]: string[] } = {
  normal: [
    PRODUCT_CODE_MAPPING.sleep_support_24_pk,
    PRODUCT_CODE_MAPPING.focus_support_24_pk,
  ],
  type_1: [
    PRODUCT_CODE_MAPPING.stress_support_24_pk,
    PRODUCT_CODE_MAPPING.sleep_support_24_pk,
  ],
  type_2: [
    PRODUCT_CODE_MAPPING.sleep_support_24_pk,
    PRODUCT_CODE_MAPPING.stress_support_24_pk,
  ],
  type_3: [
    PRODUCT_CODE_MAPPING.stress_support_24_pk,
    PRODUCT_CODE_MAPPING.focus_support_24_pk,
  ],
};

export const PersonalizedCart = ({
  snapShot,
}: {
  snapShot: HormoneSnapshot;
}): JSX.Element => {
  const classes = useStyles();
  const { state, removeFromCart, addToCart } = useGlobalState(true);

  const cart = state.cart;

  const initialProducts = useMemo(() => {
    const defaultProducts = recommendedProducts[snapShot?.result] || [];

    return defaultProducts;
  }, [snapShot, recommendedProducts]);

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(true);
  const [checkoutLink, setCheckoutLink] = useState<string>('');
  const [cartTotal, setCartTotal] = useState<number>(0);

  const removeProduct = (productCode: string) => {
    if (state?.products?.size > 0) {
      const productToRemove = state?.products?.get(productCode);
      if (productToRemove) {
        removeFromCart(productToRemove);
      }

      setCheckoutUrl();
    }
  };

  const setCheckoutUrl = useCallback(() => {
    const domain = SHOPIFY_WEB_URL;
    let itemsString = '';
    let total = 0;

    cart.forEach((item, index) => {
      const productDetails = SHOPIFY_VARIANT_MAPPINGS[item.code] || {};
      const { id, selling_plan } = productDetails;
      const quantity = 1; // Default quantity to 1

      if (id) {
        let itemParams = `items[${index}][quantity]=${quantity}%26items[${index}][selling_plan]=${selling_plan}%26items[${index}][id]=${id}`;
        itemsString += index === 0 ? itemParams : `%26${itemParams}`;
      }
      total += item.price || 0;
    });

    const discountCode = '';
    const encodedDiscount = discountCode
      ? `?discount=${encodeURIComponent(discountCode)}`
      : '';
    const checkoutBaseUrl = `${domain}/cart/clear?return_to=/cart/add?${itemsString}%26return_to=/checkout${encodedDiscount}`;

    setCheckoutLink(checkoutBaseUrl);
    setCartTotal(total);
  }, [cart]);

  const getPrice = (item: Product) => {
    let result = Number.isSafeInteger(item.price)
      ? `$${item.price}`
      : `$${item.price}0`;
    if (item.subscription_term) {
      result += ' ' + item.textPriceLine1;
    }
    return result;
  };

  useEffect(() => {
    // Initialize cart with default products
    if (state?.products?.size > 0) {
      initialProducts?.forEach((productCode) => {
        const product: Product | undefined = state?.products?.get(productCode);
        if (product) {
          addToCart(product);
        }
      });
    }
  }, [initialProducts, state?.products]);

  useEffect(() => {
    setCheckoutUrl();
  }, [cart, setCheckoutUrl]);

  return (
    <>
      <Accordion
        disableGutters
        elevation={0}
        expanded={isAccordionExpanded}
        onChange={() => setIsAccordionExpanded(!isAccordionExpanded)}
        sx={{
          py: 1.5,
          background: colors.greys.BG_WHITE,
          '&.MuiPaper-root': {
            borderRadius: 0,
          },
        }}
      >
        <AccordionSummary
          classes={{
            expandIconWrapper: classes.expandIcon,
            expanded: classes.expanded,
          }}
          expandIcon={
            <SvgIcon
              component={ChevronIcon}
              inheritViewBox
              sx={{
                width: 20,
                height: 20,
                fill: colors.greys.BASE_WHITE,
              }}
            />
          }
          aria-controls={`personalized-cart-content`}
          id={`personalized-cart-header`}
          sx={{
            borderBottom: `1px solid ${colors.greys.BORDER_GREY}`,
            '.MuiAccordionSummary-root': {
              alignItems: 'center',
            },
            '.MuiAccordionSummary-content': {
              justifyContent: 'center',
            },
          }}
        >
          <Box display="flex" flexDirection="row" alignItems="center">
            <Typography variant="medium" fontSize={16}>
              Your personalized recommendations
            </Typography>
            {cart.length > 0 && (
              <Box
                sx={{
                  background: colors.brand.PRIMARY_RED,
                  color: colors.greys.BASE_WHITE,
                  borderRadius: 50,
                  px: 1,
                  py: 0.25,
                  ml: 0.75,
                }}
              >
                <Typography variant="medium" fontSize={14}>
                  {cart.length}
                </Typography>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ mt: 4 }}>
          {cart.map((product: Product) => (
            <Box key={product.code}>
              <Box display="flex" flexDirection="row">
                <Box>
                  <RootineImage src={product.heroImage[0].src} size="sm" />
                </Box>
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ ml: 3, width: '100%' }}
                >
                  <Typography variant="bold" fontSize={16}>
                    {product.title}
                  </Typography>
                  <Typography variant="light" fontSize={16} sx={{ my: 2 }}>
                    {getPrice(product)}
                  </Typography>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    onClick={() => removeProduct(product.code)}
                  >
                    <CustomIcon
                      name="trashcan"
                      hex="black"
                      sx={{
                        width: 20,
                        height: 20,
                        fill: colors.greys.BASE_WHITE,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
              <RootineDivider dividerProps={{ sx: { my: 3.5 } }} />
            </Box>
          ))}

          <Box display="flex" flexDirection="column">
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="light" fontSize={16}>
                Subtotal
              </Typography>
              <Typography variant="bold" fontSize={16}>
                {`$${cartTotal.toFixed(2)}`}
              </Typography>
            </Box>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 1 }}
            >
              <Typography variant="light" fontSize={16} sx={{}}>
                Shipping
              </Typography>
              <Typography variant="bold" fontSize={16} sx={{}}>
                Free
              </Typography>
            </Box>
          </Box>

          <RootineDivider dividerProps={{ sx: { my: 3.5 } }} />

          <Box display="flex" flexDirection="column">
            <RootineButton
              id="buy-now-btn"
              variation="primary"
              size="medium"
              label={`$${cartTotal.toFixed(2)} - Checkout`}
              href={checkoutLink}
              openNewTab
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
